import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetInvoiceReceiptList,
  GetInvoiceReceiptDetail,
  CreateInvoiceReceipt,
  UpdateInvoiceReceipt,
  DeleteInvoiceReceipt,
} from "../../Services/InvoiceReceiptService";

export interface SalesReceiptState {
  invoiceReceiptCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  selectedInvoiceReceiptDetail: any | null;
  totalAmount: number;
  totalKasarAmount: number;
  totalPayAmount: number;
  totalTdsAmount: number;
  purchaseStatusDataCollection: any[];
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;
  error: string | null;
  errorDetail: string | null;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  cache: Map<string, CacheEntry>;
  requestAbortController: AbortController | null;
  lastFetchParams: FetchParams | null;
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}

interface FetchParams {
  consumerId: number;
  firmId: number;
  branchId: number;
  startDate: string;
  endDate: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  financialYear: string;
  buyerIds: string;
  salereceipttype: string;
  accountIds: string;
  partyTypeIds: string;
  labelIds: string;
}

const CACHE_DURATION = 5 * 60 * 1000;

const generateCacheKey = (params: FetchParams): string => {
  return `sales_receipt_${params.consumerId}_${params.firmId}_${params.branchId}_${params.pageIndex}_${params.searchValue}_${params.salereceipttype}`;
};

const isCacheValid = (entry: CacheEntry): boolean => {
  return Date.now() - entry.timestamp < entry.expiresIn;
};

export const fetchSalesReceiptList = createAsyncThunk(
  "salesReceipt/fetchList",
  async (params: FetchParams, { rejectWithValue, getState }) => {
    try {
      const root = getState() as { salesReceipt: SalesReceiptState };
      const cacheKey = generateCacheKey(params);

      if (root.salesReceipt.cache.has(cacheKey)) {
        const cached = root.salesReceipt.cache.get(cacheKey)!;
        if (isCacheValid(cached)) {
          return cached.data;
        }
      }

      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("startDate", params.startDate);
      urlParams.append("endDate", params.endDate);
      urlParams.append("pageIndex", params.pageIndex.toString());
      urlParams.append("pageSize", params.pageSize.toString());
      urlParams.append("sortColumn", params.sortColumn);
      urlParams.append("sortDirection", params.sortDirection);
      urlParams.append("searchValue", params.searchValue);
      urlParams.append("financialYear", params.financialYear);
      urlParams.append("buyerIds", params.buyerIds);
      urlParams.append("salereceipttype", params.salereceipttype);
      urlParams.append("accountIds", params.accountIds);
      urlParams.append("partyTypeIds", params.partyTypeIds);
      urlParams.append("labelIds", params.labelIds);

      const result: any = await GetInvoiceReceiptList(urlParams);

      if (result?.data?.data) {
        return result.data.data;
      }

      return rejectWithValue("No sales receipt data received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || "Failed to fetch sales receipts"
      );
    }
  }
);

export const fetchSalesReceiptDetail = createAsyncThunk(
  "salesReceipt/fetchDetail",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      invoiceReceiptId: number;
      financialYear: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("invoiceReceiptId", params.invoiceReceiptId.toString());
      urlParams.append("financialYear", params.financialYear);

      const result: any = await GetInvoiceReceiptDetail(urlParams);

      if (result?.data?.data) {
        return result.data.data;
      }

      return rejectWithValue("No sales receipt detail received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || "Failed to fetch sales receipt details"
      );
    }
  }
);

export const createSalesReceiptAsync = createAsyncThunk(
  "salesReceipt/create",
  async (data: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateInvoiceReceipt(data);
      if (result?.data) {
        return result.data;
      }
      return rejectWithValue("Failed to create sales receipt");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || "Failed to create sales receipt"
      );
    }
  }
);

export const updateSalesReceiptAsync = createAsyncThunk(
  "salesReceipt/update",
  async (data: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateInvoiceReceipt(data);
      if (result?.data) {
        return result.data;
      }
      return rejectWithValue("Failed to update sales receipt");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || "Failed to update sales receipt"
      );
    }
  }
);

export const deleteSalesReceiptAsync = createAsyncThunk(
  "salesReceipt/delete",
  async (deleteData: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteInvoiceReceipt(deleteData);
      if (result?.data || result?.Data) {
        return deleteData;
      }
      return rejectWithValue("Failed to delete sales receipt");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || "Failed to delete sales receipt"
      );
    }
  }
);

const initialState: SalesReceiptState = {
  invoiceReceiptCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 10,
  selectedInvoiceReceiptDetail: null,
  totalAmount: 0,
  totalKasarAmount: 0,
  totalPayAmount: 0,
  totalTdsAmount: 0,
  purchaseStatusDataCollection: [],
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  error: null,
  errorDetail: null,
  sortColumn: "",
  sortDirection: "asc",
  searchValue: "",
  cache: new Map(),
  requestAbortController: null,
  lastFetchParams: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

const salesReceiptSlice = createSlice({
  name: "salesReceipt",
  initialState,
  reducers: {
    clearSalesReceiptData: (state) => {
      state.invoiceReceiptCollection = [];
      state.totalCount = 0;
      state.selectedInvoiceReceiptDetail = null;
      state.error = null;
    },
    clearSalesReceiptDetail: (state) => {
      state.selectedInvoiceReceiptDetail = null;
      state.errorDetail = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.errorDetail = null;
    },
    clearSuccessFlags: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },
    invalidateCache: (state) => {
      state.cache.clear();
    },
    setFetchMetadata: (state, action: PayloadAction<Partial<FetchParams>>) => {
      state.searchValue = action.payload.searchValue || state.searchValue;
      state.sortColumn = action.payload.sortColumn || state.sortColumn;
      state.sortDirection = action.payload.sortDirection || state.sortDirection;
      state.pageIndex = action.payload.pageIndex || state.pageIndex;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesReceiptList.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchSalesReceiptList.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingList = false;
        state.invoiceReceiptCollection = action.payload?.invoiceReceiptCollection || [];
        state.totalCount = action.payload?.totalCount || 0;

        const purchaseStatus = action.payload?.purchaseStatusDataCollection || [];
        state.purchaseStatusDataCollection = purchaseStatus;

        // Totals are derived from `purchaseStatusDataCollection` in the UI
        // and/or selectors; avoid recomputing them here to prevent
        // duplication and possible mismatch with per-tab totals.

        if (state.lastFetchParams) {
          const cacheKey = generateCacheKey(state.lastFetchParams);
          state.cache.set(cacheKey, {
            data: action.payload,
            timestamp: Date.now(),
            expiresIn: CACHE_DURATION,
          });
        }
      })
      .addCase(fetchSalesReceiptList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchSalesReceiptDetail.pending, (state) => {
        state.loadingDetail = true;
        state.errorDetail = null;
      })
      .addCase(fetchSalesReceiptDetail.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingDetail = false;
        state.selectedInvoiceReceiptDetail = action.payload;
      })
      .addCase(fetchSalesReceiptDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.errorDetail = action.payload as string;
      });

    builder
      .addCase(createSalesReceiptAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createSalesReceiptAsync.fulfilled, (state) => {
        state.loadingCreate = false;
        state.createSuccess = true;
        state.cache.clear();
      })
      .addCase(createSalesReceiptAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateSalesReceiptAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateSalesReceiptAsync.fulfilled, (state) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear();
      })
      .addCase(updateSalesReceiptAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteSalesReceiptAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteSalesReceiptAsync.fulfilled, (state) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
        state.cache.clear();
      })
      .addCase(deleteSalesReceiptAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearSalesReceiptData,
  clearSalesReceiptDetail,
  clearErrors,
  clearSuccessFlags,
  invalidateCache,
  setFetchMetadata,
} = salesReceiptSlice.actions;

export default salesReceiptSlice.reducer;
